import {FC, useEffect, useState, useMemo, useCallback} from "react";
import {Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Typography, TablePagination, Card, CardContent, styled, TableSortLabel, Avatar, IconButton, Popover, Button, Grid, Tooltip} from "@mui/material";
import {AdminHeader} from "../../AdminHeader";
import {useApi} from "@buildwitt/shapeshift-identity";
import dayjs from "dayjs";
import {pdf} from "@react-pdf/renderer";
import {ScheduledReportsPDFDocument} from "./ScheduledReportsPDF";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import {useSSTranslation} from "@/i18n/useSSTranslation";

export const AdminScheduledReportsPage: FC = () => {
	const {t} = useSSTranslation();
	
	// Selected month state (defaults to last full month)
	const [selectedMonth, setSelectedMonth] = useState(() => dayjs().subtract(1, 'month'));
	
	// Month picker popover state
	const [monthPickerAnchor, setMonthPickerAnchor] = useState<HTMLElement | null>(null);
	const [pickerYear, setPickerYear] = useState(() => selectedMonth.year());
	const monthPickerOpen = Boolean(monthPickerAnchor);
	
	const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

	const [usersWithoutPage, setUsersWithoutPage] = useState(1);
	const [divisionsPage, setDivisionsPage] = useState(1);
	const [supervisorsPage, setSupervisorsPage] = useState(1);
	const pageSize = 10;

	// Sorting state
	type SortOrder = 'asc' | 'desc';
	const [divisionsSort, setDivisionsSort] = useState<{column: string | null, order: SortOrder}>({column: 'Avg. Completed Lessons', order: 'desc'});
	const [supervisorsSort, setSupervisorsSort] = useState<{column: string | null, order: SortOrder}>({column: 'Avg. Completed Lessons', order: 'desc'});
	const [usersWithoutSort, setUsersWithoutSort] = useState<{column: string | null, order: SortOrder}>({column: null, order: 'asc'});

	// Fetch available saved reports
	interface SavedReport {
		id: number;
		monthId: number;
		yearId: number;
		monthName: string;
	}
	interface ReportData {
		monthName: string;
		accountName: string;
		overview: any;
		topPerformers: any[];
		usersWithout: any[];
		divisions: any[];
		supervisors: any[];
	}
	interface SavedReportWithData {
		id: number;
		monthId: number;
		yearId: number;
		reportData: ReportData;
	}
	
	const [savedReportsResponse, fetchSavedReports] = useApi<SavedReport[]>();
	const [reportDataResponse, fetchReportData] = useApi<SavedReportWithData>();
	const [generateResponse, generateReport] = useApi<SavedReport>();
	const [generateError, setGenerateError] = useState<string | null>(null);
	const [isDownloading, setIsDownloading] = useState(false);
	const [downloadError, setDownloadError] = useState<string | null>(null);
	
	// Fetch saved reports on mount
	useEffect(() => {
		fetchSavedReports({
			method: "GET",
			url: "/api/v1/scheduled-reports/saved"
		});
	}, []);
	
	// Get available months from saved reports
	const availableMonths = useMemo(() => {
		if (!savedReportsResponse.data) return [];
		return savedReportsResponse.data.map(report => ({
			month: report.monthId - 1, // Convert 1-indexed to 0-indexed
			year: report.yearId,
			label: report.monthName
		}));
	}, [savedReportsResponse.data]);
	
	// Get years that have reports
	const availableYears = useMemo(() => {
		const years = new Set(availableMonths.map(m => m.year));
		return Array.from(years).sort((a, b) => b - a);
	}, [availableMonths]);
	
	// Check if a month/year has a saved report
	const hasReport = useCallback((monthIndex: number, year: number) => {
		return availableMonths.some(m => m.month === monthIndex && m.year === year);
	}, [availableMonths]);
	
	// Set initial selected month to most recent available report
	useEffect(() => {
		if (availableMonths.length > 0) {
			// Sort by date descending and pick the most recent
			const sorted = [...availableMonths].sort((a, b) => {
				const dateA = dayjs().year(a.year).month(a.month);
				const dateB = dayjs().year(b.year).month(b.month);
				return dateB.diff(dateA);
			});
			const mostRecent = sorted[0];
			setSelectedMonth(dayjs().year(mostRecent.year).month(mostRecent.month));
			setPickerYear(mostRecent.year);
		}
	}, [availableMonths.length]); // Only run when availableMonths is first loaded
	
	// Calculate date range from selected month
	const dateStart = selectedMonth.startOf('month').format('YYYY-MM-DD');
	const dateEnd = selectedMonth.endOf('month').format('YYYY-MM-DD');
	
	// Month navigation handlers - navigate between available reports
	const goToPreviousMonth = useCallback(() => {
		// Find the next older report
		const olderReports = availableMonths.filter(m => 
			dayjs().year(m.year).month(m.month).isBefore(selectedMonth, 'month')
		).sort((a, b) => {
			const dateA = dayjs().year(a.year).month(a.month);
			const dateB = dayjs().year(b.year).month(b.month);
			return dateB.diff(dateA); // Most recent first
		});
		if (olderReports.length > 0) {
			const prev = olderReports[0];
			setSelectedMonth(dayjs().year(prev.year).month(prev.month));
		}
	}, [selectedMonth, availableMonths]);
	
	const goToNextMonth = useCallback(() => {
		// Find the next newer report
		const newerReports = availableMonths.filter(m => 
			dayjs().year(m.year).month(m.month).isAfter(selectedMonth, 'month')
		).sort((a, b) => {
			const dateA = dayjs().year(a.year).month(a.month);
			const dateB = dayjs().year(b.year).month(b.month);
			return dateA.diff(dateB); // Oldest first
		});
		if (newerReports.length > 0) {
			const next = newerReports[0];
			setSelectedMonth(dayjs().year(next.year).month(next.month));
		}
	}, [selectedMonth, availableMonths]);
	
	// Check if we can navigate
	const canGoPrevious = availableMonths.some(m => 
		dayjs().year(m.year).month(m.month).isBefore(selectedMonth, 'month')
	);
	const canGoNext = availableMonths.some(m => 
		dayjs().year(m.year).month(m.month).isAfter(selectedMonth, 'month')
	);
	
	const handleMonthPickerOpen = (event: React.MouseEvent<HTMLElement>) => {
		setPickerYear(selectedMonth.year());
		setMonthPickerAnchor(event.currentTarget);
	};
	
	const handleMonthPickerClose = () => {
		setMonthPickerAnchor(null);
	};
	
	const handleMonthSelect = (monthIndex: number) => {
		// Only allow selecting months that have saved reports
		if (hasReport(monthIndex, pickerYear)) {
			setSelectedMonth(dayjs().year(pickerYear).month(monthIndex));
			handleMonthPickerClose();
		}
	};
	
	const handlePickerYearChange = (delta: number) => {
		const newYear = pickerYear + delta;
		// Only allow years that have reports
		if (availableYears.includes(newYear)) {
			setPickerYear(newYear);
		}
	};
	
	const canGoToPreviousYear = availableYears.some(y => y < pickerYear);
	const canGoToNextYear = availableYears.some(y => y > pickerYear);

	// Reset page to 1 when sorting changes
	useEffect(() => {
		setDivisionsPage(1);
	}, [divisionsSort]);

	useEffect(() => {
		setSupervisorsPage(1);
	}, [supervisorsSort]);

	useEffect(() => {
		setUsersWithoutPage(1);
	}, [usersWithoutSort]);

	// Find the saved report ID for the selected month and fetch its data
	const selectedReportId = useMemo(() => {
		if (!savedReportsResponse.data) return null;
		const report = savedReportsResponse.data.find(
			r => r.monthId === selectedMonth.month() + 1 && r.yearId === selectedMonth.year()
		);
		return report?.id ?? null;
	}, [savedReportsResponse.data, selectedMonth]);
	
	// Fetch report data when selected report changes
	useEffect(() => {
		if (selectedReportId) {
			fetchReportData({
				method: "GET",
				url: `/api/v1/scheduled-reports/saved/${selectedReportId}`
			});
		}
	}, [selectedReportId]);
	
	// Handle generate report
	const lastMonth = dayjs().subtract(1, 'month');
	const canGenerateLastMonth = !savedReportsResponse.data?.some(
		r => r.monthId === lastMonth.month() + 1 && r.yearId === lastMonth.year()
	);
	
	const handleGenerateReport = useCallback(() => {
		setGenerateError(null);
		generateReport({
			method: "POST",
			url: "/api/v1/scheduled-reports/generate",
			config: {
				data: {
					month: lastMonth.month() + 1,
					year: lastMonth.year()
				}
			}
		});
	}, [lastMonth, generateReport]);
	
	// Refetch saved reports after successful generation
	useEffect(() => {
		if (generateResponse.data && !generateResponse.isLoading && !generateResponse.isError) {
			setGenerateError(null);
			fetchSavedReports({
				method: "GET",
				url: "/api/v1/scheduled-reports/saved"
			});
		}
		if (generateResponse.isError) {
			setGenerateError(generateResponse.errorData?.error?.message || "Failed to generate report");
		}
	}, [generateResponse.data, generateResponse.isLoading, generateResponse.isError]);
	
	// Extract data from saved report
	const reportData = reportDataResponse.data?.reportData;
	const overviewData = reportData?.overview;
	const topPerformersData = reportData?.topPerformers || [];
	const usersWithoutData = reportData?.usersWithout || [];
	const divisionsData = reportData?.divisions || [];
	const supervisorsData = reportData?.supervisors || [];

	const isLoading = savedReportsResponse.isLoading || reportDataResponse.isLoading;
	const hasNoReports = !savedReportsResponse.isLoading && (!savedReportsResponse.data || savedReportsResponse.data.length === 0);

	const monthName = dayjs(dateStart).format("MMMM YYYY");
	
	// Handle PDF download
	const handleDownloadReport = useCallback(async () => {
		if (!reportData) {
			setDownloadError("No report data available for download");
			return;
		}
		
		setIsDownloading(true);
		setDownloadError(null);
		try {
			// Sort divisions and supervisors by Avg. Completed Lessons descending
			const sortedDivisions = [...(divisionsData || [])].sort(
				(a, b) => (b["Avg. Completed Lessons"] || 0) - (a["Avg. Completed Lessons"] || 0)
			);
			const sortedSupervisors = [...(supervisorsData || [])].sort(
				(a, b) => (b["Avg. Completed Lessons"] || 0) - (a["Avg. Completed Lessons"] || 0)
			);

			const doc = (
				<ScheduledReportsPDFDocument
					overview={overviewData}
					topPerformers={topPerformersData}
					usersWithout={usersWithoutData}
					divisions={sortedDivisions}
					supervisors={sortedSupervisors}
					monthName={reportData.monthName}
					accountName={reportData.accountName}
				/>
			);

			const blob = await pdf(doc).toBlob();
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `${reportData.accountName}-${reportData.monthName.replace(" ", "-")}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} catch (error: any) {
			setDownloadError(error?.message || "Failed to generate PDF. Please try again.");
		} finally {
			setIsDownloading(false);
		}
	}, [reportData, overviewData, topPerformersData, usersWithoutData, divisionsData, supervisorsData]);

	// Sorting handlers
	const handleSort = (table: 'divisions' | 'supervisors' | 'usersWithout', column: string) => {
		const setState = table === 'divisions' ? setDivisionsSort : table === 'supervisors' ? setSupervisorsSort : setUsersWithoutSort;
		const currentSort = table === 'divisions' ? divisionsSort : table === 'supervisors' ? supervisorsSort : usersWithoutSort;
		
		setState({
			column,
			order: currentSort.column === column && currentSort.order === 'asc' ? 'desc' : 'asc'
		});
	};

	// Sorting functions
	const sortData = (data: any[], column: string | null, order: SortOrder) => {
		if (!column || !data) return data;
		
		return [...data].sort((a, b) => {
			let aVal = a[column];
			let bVal = b[column];
			
			// Try to parse as numbers if they're numeric strings
			const aNum = typeof aVal === 'number' ? aVal : parseFloat(String(aVal).replace(/[^0-9.-]/g, ''));
			const bNum = typeof bVal === 'number' ? bVal : parseFloat(String(bVal).replace(/[^0-9.-]/g, ''));
			
			// If both are valid numbers, sort numerically
			if (!isNaN(aNum) && !isNaN(bNum)) {
				return order === 'asc' ? aNum - bNum : bNum - aNum;
			}
			
			// Handle dates
			if (column === 'Date of Last Completion') {
				const aDate = aVal === 'Never' ? new Date(0) : new Date(aVal);
				const bDate = bVal === 'Never' ? new Date(0) : new Date(bVal);
				if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
					return order === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
				}
			}
			
			// Handle string values
			aVal = String(aVal || '').toLowerCase();
			bVal = String(bVal || '').toLowerCase();
			
			if (aVal < bVal) return order === 'asc' ? -1 : 1;
			if (aVal > bVal) return order === 'asc' ? 1 : -1;
			return 0;
		});
	};

	// Sorted and paginated data
	const sortedDivisions = useMemo(() => 
		sortData(divisionsData, divisionsSort.column, divisionsSort.order),
		[divisionsData, divisionsSort]
	);

	const sortedSupervisors = useMemo(() => 
		sortData(supervisorsData, supervisorsSort.column, supervisorsSort.order),
		[supervisorsData, supervisorsSort]
	);

	const sortedUsersWithout = useMemo(() => 
		sortData(usersWithoutData, usersWithoutSort.column, usersWithoutSort.order),
		[usersWithoutData, usersWithoutSort]
	);

	// Paginated data (slice the sorted data)
	const paginatedDivisions = useMemo(() => {
		const start = (divisionsPage - 1) * pageSize;
		return sortedDivisions.slice(start, start + pageSize);
	}, [sortedDivisions, divisionsPage, pageSize]);

	const paginatedSupervisors = useMemo(() => {
		const start = (supervisorsPage - 1) * pageSize;
		return sortedSupervisors.slice(start, start + pageSize);
	}, [sortedSupervisors, supervisorsPage, pageSize]);

	const paginatedUsersWithout = useMemo(() => {
		const start = (usersWithoutPage - 1) * pageSize;
		return sortedUsersWithout.slice(start, start + pageSize);
	}, [sortedUsersWithout, usersWithoutPage, pageSize]);

	const OverviewCard = styled(Card)(({theme}) => ({
		borderRadius: "8px",
		backgroundColor: theme.palette.background.paper,
		height: "138px",
		border: "1px solid #A1A1A026",
		boxSizing: "border-box"
	}));

	// Format hours as "XX hrs XX min" (or "XX hrs" if minutes are 0)
	const formatHours = (hours: number): string => {
		const h = Math.floor(hours);
		const m = Math.round((hours - h) * 60);
		if (m === 0) {
			return `${h} hrs`;
		}
		return `${h} hrs ${m.toString().padStart(2, "0")} min`;
	};

	const renderOverviewCard = (title: string, value: string | number, changePercent?: number, tooltipKey?: string, reverseColors = false) => {
		const hasChange = changePercent !== undefined && changePercent !== null;
		const isPositive = hasChange && changePercent > 0;
		const isNegative = hasChange && changePercent < 0;
		// For overdue lessons: up arrow (more overdue) is bad (red), down arrow (less overdue) is good (green)
		// For other metrics: up arrow is good (green), down arrow is bad (red)
		const changeColor = reverseColors 
			? (isPositive ? "#f44336" : isNegative ? "#4caf50" : undefined)
			: (isPositive ? "#4caf50" : isNegative ? "#f44336" : undefined);

		const cardContent = (
			<OverviewCard sx={{flex: {xs: "1 1 30%", lg: "1 1 22%"}}}>
				<CardContent sx={{height: "95px", position: "relative", padding: "16px !important"}}>
					<Box sx={{position: "absolute", top: "20px", left: "16px", right: "16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start"}}>
						{tooltipKey ? (
							<Tooltip title={t(tooltipKey as any, "")} arrow>
								<Typography variant="subtitle2" sx={{fontWeight: 600, cursor: "help", flex: 1, pr: 1}}>
									{title.toUpperCase()}
								</Typography>
							</Tooltip>
						) : (
							<Typography variant="subtitle2" sx={{fontWeight: 600, flex: 1, pr: 1}}>
								{title.toUpperCase()}
							</Typography>
						)}
						{hasChange && (
							<Stack direction="row" gap={0.5} alignItems="center" sx={{flexShrink: 0}}>
								{isPositive ? (
									<ArrowUpwardIcon sx={{color: changeColor, fontSize: "16px"}} />
								) : isNegative ? (
									<ArrowDownwardIcon sx={{color: changeColor, fontSize: "16px"}} />
								) : null}
								<Typography variant="body2" sx={{color: changeColor, fontWeight: 600, whiteSpace: "nowrap"}}>
									{Math.round(Math.abs(changePercent))}%
								</Typography>
							</Stack>
						)}
					</Box>
					<Box sx={{position: "absolute", bottom: "12px", width: "100%"}}>
						<Typography 
							variant="h2" 
							sx={{
								fontWeight: 600,
								fontSize: typeof value === "string" && value.includes("hrs") ? "2.5rem" : undefined
							}} 
							color="primary"
						>
							{value}
						</Typography>
					</Box>
				</CardContent>
			</OverviewCard>
		);

		return cardContent;
	};

	// Month selector button styles
	const monthSelectorButtonSx = {
		border: "1.5px solid",
		borderColor: "primary.main",
		borderRadius: 1,
		color: "primary.main",
		minWidth: 36,
		height: 36,
		p: 0,
		"&:hover": {
			backgroundColor: "rgba(255, 158, 0, 0.1)",
			borderColor: "primary.main"
		},
		"&.Mui-disabled": {
			borderColor: "rgba(255, 158, 0, 0.3)",
			color: "rgba(255, 158, 0, 0.3)"
		}
	};

	return (
		<Box sx={{display: "flex", justifyContent: "center"}}>
			<Stack sx={{maxWidth: "1444px", width: "100%"}} gap={3}>
				<Stack direction="row" justifyContent="space-between" alignItems="flex-start">
					<AdminHeader
						title="Monthly Report"
						subtitle="Team engagement overview and insights"
					/>
					<Stack direction="row" spacing={2} sx={{mt: 1}}>
						{!hasNoReports && reportData && (
							<Button
								variant="outlined"
								color="primary"
								onClick={handleDownloadReport}
								disabled={isDownloading}
								startIcon={<DownloadIcon />}
							>
								{isDownloading ? "Downloading..." : "Download Report"}
							</Button>
						)}
						{canGenerateLastMonth && (
							<Button
								variant="contained"
								color="primary"
								onClick={handleGenerateReport}
								disabled={generateResponse.isLoading}
							>
								{generateResponse.isLoading ? "Generating..." : `Generate ${lastMonth.format("MMMM YYYY")} Report`}
							</Button>
						)}
					</Stack>
				</Stack>
				
				{generateError && (
					<Typography color="error" sx={{mt: -2}}>{generateError}</Typography>
				)}
				
				{downloadError && (
					<Typography color="error" sx={{mt: -2}}>{downloadError}</Typography>
				)}
				
				{/* Month Selector */}
				<Stack direction="row" spacing={1} alignItems="center" sx={{mt: -2}}>
					<IconButton onClick={goToPreviousMonth} disabled={!canGoPrevious || hasNoReports} sx={monthSelectorButtonSx}>
						<ChevronLeftIcon sx={{fontSize: 20}} />
					</IconButton>
					
					<Box
						onClick={hasNoReports ? undefined : handleMonthPickerOpen}
						sx={{
							border: "1.5px solid",
							borderColor: hasNoReports ? "grey.600" : "primary.main",
							borderRadius: 1,
							px: 2,
							height: 36,
							boxSizing: "border-box",
							display: "flex",
							alignItems: "center",
							gap: 1,
							cursor: hasNoReports ? "default" : "pointer",
							opacity: hasNoReports ? 0.5 : 1,
							"&:hover": {
								backgroundColor: hasNoReports ? "transparent" : "rgba(255, 158, 0, 0.1)"
							}
						}}
					>
						<CalendarTodayOutlinedIcon sx={{color: hasNoReports ? "grey.600" : "primary.main", fontSize: 16}} />
						<Typography sx={{
							color: hasNoReports ? "grey.600" : "primary.main",
							fontWeight: 600,
							fontSize: 13,
							textTransform: "uppercase",
							letterSpacing: 0.5
						}}>
							{hasNoReports ? "No Data" : selectedMonth.format("MMMM YYYY")}
						</Typography>
					</Box>
					
					<IconButton onClick={goToNextMonth} disabled={!canGoNext || hasNoReports} sx={monthSelectorButtonSx}>
						<ChevronRightIcon sx={{fontSize: 20}} />
					</IconButton>
				</Stack>
				
				{/* Month Picker Popover */}
				<Popover
					open={monthPickerOpen}
					anchorEl={monthPickerAnchor}
					onClose={handleMonthPickerClose}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "center"
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "center"
					}}
					PaperProps={{
						sx: {
							backgroundColor: "#1E1E1E",
							border: "1px solid rgba(255,255,255,0.1)",
							borderRadius: 2,
							mt: 1,
							width: 200
						}
					}}
				>
					<Box sx={{p: 1.5}}>
						{/* Year selector header */}
						<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1.5, pb: 1, borderBottom: "1px solid rgba(255,255,255,0.1)", px: 1}}>
							<Button
								onClick={() => handlePickerYearChange(-1)}
								disabled={!canGoToPreviousYear}
								sx={{color: "white", minWidth: 28, p: 0.25, "&.Mui-disabled": {color: "rgba(255,255,255,0.3)"}}}
							>
								<ChevronLeftIcon fontSize="small" />
							</Button>
							<Typography sx={{color: "white", fontWeight: 500, fontSize: 14, px: 2}}>
								{pickerYear}
							</Typography>
							<Button
								onClick={() => handlePickerYearChange(1)}
								disabled={!canGoToNextYear}
								sx={{color: "white", minWidth: 28, p: 0.25, "&.Mui-disabled": {color: "rgba(255,255,255,0.3)"}}}
							>
								<ChevronRightIcon fontSize="small" />
							</Button>
						</Stack>
						
						{/* Month grid */}
						<Grid container spacing={0.5}>
							{monthNames.map((month, index) => {
								const isAvailable = hasReport(index, pickerYear);
								const isSelected = selectedMonth.month() === index && selectedMonth.year() === pickerYear;
								
								return (
									<Grid item xs={4} key={month}>
										<Button
											fullWidth
											onClick={() => handleMonthSelect(index)}
											disabled={!isAvailable}
											sx={{
												color: isSelected ? "primary.main" : !isAvailable ? "rgba(255,255,255,0.3)" : "white",
												fontWeight: isSelected ? 600 : 400,
												fontSize: 13,
												py: 0.75,
												minWidth: 0,
												"&:hover": {
													backgroundColor: isAvailable ? "rgba(255,255,255,0.1)" : "transparent"
												},
												"&.Mui-disabled": {
													color: "rgba(255,255,255,0.3)"
												}
											}}
										>
											{month}
										</Button>
									</Grid>
								);
							})}
						</Grid>
					</Box>
				</Popover>

				{isLoading ? (
					<Box sx={{display: "flex", justifyContent: "center", p: 4}}>
						<CircularProgress />
					</Box>
				) : hasNoReports ? (
					<Box sx={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 8, textAlign: "center"}}>
						<Typography variant="h5" sx={{color: "text.secondary", mb: 2}}>
							No Reports Available
						</Typography>
						<Typography variant="body1" sx={{color: "text.secondary", mb: 3}}>
							Generate your first report to see team engagement data. Please note, this mid-month generate will contain slightly different data, and scheduled reports will be generated at the beginning of every subsequent month.
						</Typography>
						{canGenerateLastMonth && (
							<Button
								variant="contained"
								color="primary"
								onClick={handleGenerateReport}
								disabled={generateResponse.isLoading}
								size="large"
							>
								{generateResponse.isLoading ? "Generating..." : `Generate ${lastMonth.format("MMMM YYYY")} Report`}
							</Button>
						)}
					</Box>
				) : (
					<>
						{/* Overview Cards */}
						<Box>
							<Stack direction="row" gap={2} sx={{flexWrap: "wrap"}}>
								{renderOverviewCard(
									"Total Watch Time",
									overviewData ? formatHours(overviewData["Total Watch Time (hours)"] || 0) : "0 hrs 00 mins",
									overviewData?.["Watch Time Change %"],
									"scheduled_reports_tooltip_total_watch_time"
								)}
								{renderOverviewCard(
									"Users with Completed Lessons",
									(() => {
										const withLessons = Number(overviewData?.["Users with Completed Lessons"]) || 0;
										const withoutLessons = Number(overviewData?.["Users without Completion"]) || 0;
										const total = withLessons + withoutLessons;
										return total > 0 ? `${((withLessons / total) * 100).toFixed(1)}%` : "0.0%";
									})(),
									overviewData?.["Users with Completed Lessons Change %"],
									"scheduled_reports_tooltip_users_with_completed_lessons"
								)}
								{renderOverviewCard(
									"Average Watch Time",
									overviewData ? formatHours(overviewData["Average Watch Time (hours)"] || 0) : "0 hrs 00 mins",
									overviewData?.["Average Watch Time Change %"],
									"scheduled_reports_tooltip_average_watch_time"
								)}
								{renderOverviewCard(
									"Completed Lessons",
									overviewData?.["Completed Lessons"] ?? 0,
									overviewData?.["Completed Lessons Change %"],
									"scheduled_reports_tooltip_completed_lessons"
								)}
								{renderOverviewCard(
									"Completed Courses",
									overviewData?.["Completed Courses"] ?? 0,
									overviewData?.["Completed Courses Change %"],
									"scheduled_reports_tooltip_completed_courses"
								)}
								{renderOverviewCard(
									"Average Streak",
									overviewData ? `${overviewData["Average Streak (days)"]} days` : "0 days",
									overviewData?.["Average Streak Change %"],
									"scheduled_reports_tooltip_average_streak"
								)}
								{renderOverviewCard(
									"Overdue Lessons",
									overviewData?.["Overdue Lessons"] ?? 0,
									overviewData?.["Overdue Lessons Change %"],
									"scheduled_reports_tooltip_overdue_lessons",
									true // reverseColors = true for overdue lessons
								)}
								{renderOverviewCard(
									"Utilization",
									overviewData ? `${overviewData["Utilization %"]}%` : "0%",
									overviewData?.["Utilization Change %"],
									"scheduled_reports_tooltip_utilization"
								)}
							</Stack>
						</Box>

						{/* Top Performers */}
						<Box>
							<Typography variant="subtitle2" sx={{mb: 2, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(255,255,255,0.7)"}}>
								Top 3 Performers
							</Typography>
							<Stack direction="row" spacing={2}>
								{(topPerformersData && topPerformersData.length > 0) ? (
									topPerformersData.map((row, idx) => (
										<Paper key={idx} sx={{p: 3, borderRadius: 2, backgroundColor: "background.paper", flex: 1}}>
											<Stack alignItems="flex-start" spacing={2}>
												<Avatar
													src={row.ProfileImage || undefined}
													sx={{
														width: 72,
														height: 72,
														border: "2px solid",
														borderColor: "primary.main"
													}}
												>
													{row.Name?.charAt(0) || "?"}
												</Avatar>
												<Typography variant="h6" sx={{fontWeight: 700}}>
													{row.Name}
												</Typography>
												<Stack spacing={0.5} sx={{width: "100%"}}>
													<Stack direction="row" spacing={1} alignItems="center">
														<PlayCircleOutlineIcon sx={{fontSize: 18, color: "rgba(255,255,255,0.7)"}} />
														<Typography variant="body2" sx={{color: "rgba(255,255,255,0.7)"}}>
															{row["Completed Lessons This Month"]} lessons completed this month
														</Typography>
													</Stack>
													<Stack direction="row" spacing={1} alignItems="center">
														<LocalFireDepartmentOutlinedIcon sx={{fontSize: 18, color: "rgba(255,255,255,0.7)"}} />
														<Typography variant="body2" sx={{color: "rgba(255,255,255,0.7)"}}>
															{row.Streak} day streak
														</Typography>
													</Stack>
													<Stack direction="row" spacing={1} alignItems="flex-start">
														<InfoOutlinedIcon sx={{fontSize: 18, color: "rgba(255,255,255,0.7)", mt: 0.5}} />
														<Stack spacing={0.5}>
															<Typography variant="body2" sx={{color: "rgba(255,255,255,0.7)"}}>
																Supervisor: {row.Supervisor || "N/A"}
															</Typography>
															<Typography variant="body2" sx={{color: "rgba(255,255,255,0.7)"}}>
																Division: {row.Division || "N/A"}
															</Typography>
														</Stack>
													</Stack>
												</Stack>
											</Stack>
										</Paper>
									))
								) : (
									[1, 2, 3].map((idx) => (
										<Paper key={idx} sx={{p: 3, borderRadius: 2, backgroundColor: "background.paper", flex: 1}}>
											<Stack alignItems="flex-start" spacing={2}>
												<Avatar
													sx={{
														width: 72,
														height: 72,
														border: "2px solid",
														borderColor: "grey.600",
														bgcolor: "grey.800"
													}}
												>
													?
												</Avatar>
												<Typography variant="h6" sx={{fontWeight: 700, color: "text.secondary"}}>
													No performer yet
												</Typography>
												<Stack spacing={0.5} sx={{width: "100%"}}>
													<Stack direction="row" spacing={1} alignItems="center">
														<LocalFireDepartmentOutlinedIcon sx={{fontSize: 18, color: "text.disabled"}} />
														<Typography variant="body2" sx={{color: "text.disabled"}}>
															0 day streak
														</Typography>
													</Stack>
													<Stack direction="row" spacing={1} alignItems="center">
														<PlayCircleOutlineIcon sx={{fontSize: 18, color: "text.disabled"}} />
														<Typography variant="body2" sx={{color: "text.disabled"}}>
															0 lessons completed this month
														</Typography>
													</Stack>
													<Stack direction="row" spacing={1} alignItems="center">
														<InfoOutlinedIcon sx={{fontSize: 18, color: "text.disabled"}} />
														<Typography variant="body2" sx={{color: "text.disabled"}}>
															No data available
														</Typography>
													</Stack>
												</Stack>
											</Stack>
										</Paper>
									))
								)}
							</Stack>
						</Box>

						{/* Division Insights */}
						<Box>
							<Typography variant="subtitle2" sx={{mb: 2, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(255,255,255,0.7)"}}>
								Division Insights
								{divisionsData.length > 0 && ` (${divisionsData.length})`}
							</Typography>
							<TableContainer component={Paper}>
								<Table sx={{"& .MuiTableCell-root": {borderColor: "rgba(255,255,255,0.1)"}}}>
									<TableHead>
										<TableRow>
											<TableCell>
												<TableSortLabel
													active={divisionsSort.column === 'Division'}
													direction={divisionsSort.column === 'Division' ? divisionsSort.order : 'asc'}
													onClick={() => handleSort('divisions', 'Division')}
												>
													<strong>Division</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={divisionsSort.column === 'Team Size'}
													direction={divisionsSort.column === 'Team Size' ? divisionsSort.order : 'asc'}
													onClick={() => handleSort('divisions', 'Team Size')}
												>
													<strong>Team Size</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={divisionsSort.column === 'Total Watch Time (hours)'}
													direction={divisionsSort.column === 'Total Watch Time (hours)' ? divisionsSort.order : 'asc'}
													onClick={() => handleSort('divisions', 'Total Watch Time (hours)')}
												>
													<strong>Total Watch Time</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={divisionsSort.column === 'Avg. Watch Time (hours)'}
													direction={divisionsSort.column === 'Avg. Watch Time (hours)' ? divisionsSort.order : 'asc'}
													onClick={() => handleSort('divisions', 'Avg. Watch Time (hours)')}
												>
													<strong>Avg. Watch Time</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={divisionsSort.column === 'Total Lesson Completed'}
													direction={divisionsSort.column === 'Total Lesson Completed' ? divisionsSort.order : 'asc'}
													onClick={() => handleSort('divisions', 'Total Lesson Completed')}
												>
													<strong>Total Lesson Completed</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={divisionsSort.column === 'Avg. Completed Lessons'}
													direction={divisionsSort.column === 'Avg. Completed Lessons' ? divisionsSort.order : 'asc'}
													onClick={() => handleSort('divisions', 'Avg. Completed Lessons')}
												>
													<strong>Avg. Completed Lessons</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={divisionsSort.column === 'Overdue Lessons'}
													direction={divisionsSort.column === 'Overdue Lessons' ? divisionsSort.order : 'asc'}
													onClick={() => handleSort('divisions', 'Overdue Lessons')}
												>
													<strong>Overdue Lessons</strong>
												</TableSortLabel>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{paginatedDivisions.map((row, idx) => (
											<TableRow key={idx}>
												<TableCell>{row.Division}</TableCell>
												<TableCell>{row["Team Size"]}</TableCell>
												<TableCell>{formatHours(row["Total Watch Time (hours)"] || 0)}</TableCell>
												<TableCell>{formatHours(row["Avg. Watch Time (hours)"] || 0)}</TableCell>
												<TableCell>{row["Total Lesson Completed"]}</TableCell>
												<TableCell>{row["Avg. Completed Lessons"]}</TableCell>
												<TableCell>{row["Overdue Lessons"]}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
								<TablePagination
									component="div"
									count={sortedDivisions.length}
									page={divisionsPage - 1}
									onPageChange={(_, newPage) => setDivisionsPage(newPage + 1)}
									rowsPerPage={pageSize}
									rowsPerPageOptions={[]}
								/>
							</TableContainer>
						</Box>

						{/* Supervisor Insights */}
						<Box>
							<Typography variant="subtitle2" sx={{mb: 2, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(255,255,255,0.7)"}}>
								Supervisor Insights
								{supervisorsData.length > 0 && ` (${supervisorsData.length})`}
							</Typography>
							<TableContainer component={Paper}>
								<Table sx={{"& .MuiTableCell-root": {borderColor: "rgba(255,255,255,0.1)"}}}>
									<TableHead>
										<TableRow>
											<TableCell>
												<TableSortLabel
													active={supervisorsSort.column === 'Supervisor'}
													direction={supervisorsSort.column === 'Supervisor' ? supervisorsSort.order : 'asc'}
													onClick={() => handleSort('supervisors', 'Supervisor')}
												>
													<strong>Supervisor</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={supervisorsSort.column === 'Team Size'}
													direction={supervisorsSort.column === 'Team Size' ? supervisorsSort.order : 'asc'}
													onClick={() => handleSort('supervisors', 'Team Size')}
												>
													<strong>Team Size</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={supervisorsSort.column === 'Total Watch Time (hours)'}
													direction={supervisorsSort.column === 'Total Watch Time (hours)' ? supervisorsSort.order : 'asc'}
													onClick={() => handleSort('supervisors', 'Total Watch Time (hours)')}
												>
													<strong>Total Watch Time</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={supervisorsSort.column === 'Avg. Watch Time (hours)'}
													direction={supervisorsSort.column === 'Avg. Watch Time (hours)' ? supervisorsSort.order : 'asc'}
													onClick={() => handleSort('supervisors', 'Avg. Watch Time (hours)')}
												>
													<strong>Avg. Watch Time</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={supervisorsSort.column === 'Total Completed Lessons'}
													direction={supervisorsSort.column === 'Total Completed Lessons' ? supervisorsSort.order : 'asc'}
													onClick={() => handleSort('supervisors', 'Total Completed Lessons')}
												>
													<strong>Total Completed Lessons</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={supervisorsSort.column === 'Avg. Completed Lessons'}
													direction={supervisorsSort.column === 'Avg. Completed Lessons' ? supervisorsSort.order : 'asc'}
													onClick={() => handleSort('supervisors', 'Avg. Completed Lessons')}
												>
													<strong>Avg. Completed Lessons</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={supervisorsSort.column === 'Overdue Lessons'}
													direction={supervisorsSort.column === 'Overdue Lessons' ? supervisorsSort.order : 'asc'}
													onClick={() => handleSort('supervisors', 'Overdue Lessons')}
												>
													<strong>Overdue Lessons</strong>
												</TableSortLabel>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{paginatedSupervisors.map((row, idx) => (
											<TableRow key={idx}>
												<TableCell>{row.Supervisor}</TableCell>
												<TableCell>{row["Team Size"]}</TableCell>
												<TableCell>{formatHours(row["Total Watch Time (hours)"] || 0)}</TableCell>
												<TableCell>{formatHours(row["Avg. Watch Time (hours)"] || 0)}</TableCell>
												<TableCell>{row["Total Completed Lessons"]}</TableCell>
												<TableCell>{row["Avg. Completed Lessons"]}</TableCell>
												<TableCell>{row["Overdue Lessons"]}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
								<TablePagination
									component="div"
									count={sortedSupervisors.length}
									page={supervisorsPage - 1}
									onPageChange={(_, newPage) => setSupervisorsPage(newPage + 1)}
									rowsPerPage={pageSize}
									rowsPerPageOptions={[]}
								/>
							</TableContainer>
						</Box>

						{/* Users Without Completions */}
						<Box>
							<Typography variant="subtitle2" sx={{mb: 2, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(255,255,255,0.7)"}}>
								Users Without Lesson Completions
								{usersWithoutData.length > 0 && ` (${usersWithoutData.length})`}
							</Typography>
							<TableContainer component={Paper}>
								<Table sx={{"& .MuiTableCell-root": {borderColor: "rgba(255,255,255,0.1)"}}}>
									<TableHead>
										<TableRow>
											<TableCell>
												<TableSortLabel
													active={usersWithoutSort.column === 'Name'}
													direction={usersWithoutSort.column === 'Name' ? usersWithoutSort.order : 'asc'}
													onClick={() => handleSort('usersWithout', 'Name')}
												>
													<strong>Name</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={usersWithoutSort.column === 'Division'}
													direction={usersWithoutSort.column === 'Division' ? usersWithoutSort.order : 'asc'}
													onClick={() => handleSort('usersWithout', 'Division')}
												>
													<strong>Division</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={usersWithoutSort.column === 'Supervisor'}
													direction={usersWithoutSort.column === 'Supervisor' ? usersWithoutSort.order : 'asc'}
													onClick={() => handleSort('usersWithout', 'Supervisor')}
												>
													<strong>Supervisor</strong>
												</TableSortLabel>
											</TableCell>
											<TableCell>
												<TableSortLabel
													active={usersWithoutSort.column === 'Date of Last Completion'}
													direction={usersWithoutSort.column === 'Date of Last Completion' ? usersWithoutSort.order : 'asc'}
													onClick={() => handleSort('usersWithout', 'Date of Last Completion')}
												>
													<strong>Date of Last Completion</strong>
												</TableSortLabel>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{paginatedUsersWithout.map((row, idx) => (
											<TableRow key={idx}>
												<TableCell>{row.Name}</TableCell>
												<TableCell>{row.Division}</TableCell>
												<TableCell>{row.Supervisor}</TableCell>
												<TableCell>{row["Date of Last Completion"] ? dayjs(row["Date of Last Completion"]).format('YYYY-MM-DD') : 'Never'}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
								<TablePagination
									component="div"
									count={sortedUsersWithout.length}
									page={usersWithoutPage - 1}
									onPageChange={(_, newPage) => setUsersWithoutPage(newPage + 1)}
									rowsPerPage={pageSize}
									rowsPerPageOptions={[]}
								/>
							</TableContainer>
						</Box>
					</>
				)}
			</Stack>
		</Box>
	);
};

